import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Pencil, Trash2, GitBranch, X } from 'lucide-react';
import { apiListBoms, apiCreateBom, apiUpdateBom, apiDeleteBom, apiListProducts, apiListPartners } from '../../erpApi';
import { toast } from 'sonner';

const BomForm = ({ initial, products, customers, onSubmit, onCancel }) => {
  const isEdit = !!initial;
  const [customerId, setCustomerId] = useState(initial?.customer_id || '');
  const finished = products.filter(p =>
    (p.kind === 'finished' || p.kind === 'semi_finished') &&
    (!customerId || p.customer_id === customerId)
  );
  const components = products.filter(p => p.kind !== 'finished' && p.kind !== 'service');
  const [productId, setProductId] = useState(initial?.product_id || '');
  const [variantName, setVariantName] = useState(initial?.variant_name || 'standard');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [items, setItems] = useState(
    initial?.items?.length
      ? initial.items.map((it, i) => ({ _rid: `i${i}`, product_id: it.product_id, quantity: it.quantity, notes: it.notes || '' }))
      : [{ _rid: 'i0', product_id: '', quantity: 1, notes: '' }]
  );
  const addItem = () => setItems(prev => [...prev, { _rid: `i${Date.now()}`, product_id: '', quantity: 1, notes: '' }]);
  const updItem = (i, k, v) => setItems(p => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const delItem = (i) => setItems(p => p.filter((_, idx) => idx !== i));
  const submit = (e) => {
    e.preventDefault();
    if (!isEdit && !customerId) return toast.error('Seleziona prima il cliente');
    if (!productId) return toast.error('Seleziona prodotto finito');
    const clean = items.filter(it => it.product_id && parseFloat(it.quantity) > 0);
    if (clean.length === 0) return toast.error('Aggiungi almeno un componente');
    onSubmit({
      product_id: productId, variant_name: variantName, notes,
      customer_id: customerId || null,
      items: clean.map(it => ({ product_id: it.product_id, quantity: parseFloat(it.quantity), notes: it.notes || '' }))
    });
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="bom-form">
      <div className="space-y-1.5"><Label>Cliente *</Label>
        <Select value={customerId} onValueChange={(v) => { setCustomerId(v); setProductId(''); }} disabled={isEdit}>
          <SelectTrigger data-testid="bom-customer"><SelectValue placeholder="Seleziona cliente..." /></SelectTrigger>
          <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>[{c.code}] {c.business_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Prodotto finito *</Label>
          <Select value={productId} onValueChange={setProductId} disabled={isEdit || !customerId}>
            <SelectTrigger data-testid="bom-product"><SelectValue placeholder={customerId ? 'Seleziona...' : 'Prima scegli un cliente'} /></SelectTrigger>
            <SelectContent>{finished.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Variante</Label><Input value={variantName} onChange={(e) => setVariantName(e.target.value)} placeholder="standard" /></div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between"><Label>Componenti *</Label><Button type="button" size="sm" variant="outline" onClick={addItem} className="h-8"><Plus className="w-3 h-3 mr-1" />Aggiungi</Button></div>
        {items.map((it, idx) => (
          <div key={it._rid} className="grid grid-cols-12 gap-2 p-2 bg-slate-50 rounded border border-slate-200">
            <div className="col-span-6">
              <Select value={it.product_id} onValueChange={(v) => updItem(idx, 'product_id', v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Componente..." /></SelectTrigger>
                <SelectContent>{components.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name} ({p.unit_of_measure})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-3"><Input type="number" step="0.001" min="0" value={it.quantity} onChange={(e) => updItem(idx, 'quantity', e.target.value)} placeholder="Q.tà" className="h-9 text-sm" /></div>
            <div className="col-span-2"><Input value={it.notes} onChange={(e) => updItem(idx, 'notes', e.target.value)} placeholder="Note" className="h-9 text-sm" /></div>
            <div className="col-span-1 flex items-center">{items.length > 1 && <Button type="button" size="sm" variant="ghost" className="h-9 w-9 p-0 text-red-600" onClick={() => delItem(idx)}><X className="w-4 h-4" /></Button>}</div>
          </div>
        ))}
      </div>
      <DialogFooter><Button type="button" variant="outline" onClick={onCancel}>Annulla</Button><Button data-testid="bom-submit" type="submit" className="bg-blue-700 hover:bg-blue-800">{isEdit ? 'Salva' : 'Crea'}</Button></DialogFooter>
    </form>
  );
};

const BomManager = () => {
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const reload = async () => {
    const params = customerFilter !== 'all' ? { customer_id: customerFilter } : {};
    const [b, p, c] = await Promise.all([
      apiListBoms(params),
      apiListProducts(),
      apiListPartners({ kind: 'customer' })
    ]);
    setList(b); setProducts(p); setCustomers(c);
  };
  useEffect(() => { reload(); }, [customerFilter]); // eslint-disable-line
  const handleSubmit = async (data) => {
    try {
      if (editing) await apiUpdateBom(editing.id, data); else await apiCreateBom(data);
      toast.success('OK'); setDialogOpen(false); setEditing(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const del = async (b) => {
    if (!window.confirm(`Eliminare la distinta di ${b.product_name}?`)) return;
    await apiDeleteBom(b.id); reload();
  };
  return (
    <div className="space-y-3" data-testid="bom-manager">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger data-testid="bom-customer-filter" className="h-9 w-[220px] text-sm"><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.business_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-sm text-slate-500 hidden md:block">Distinte base — componenti necessari per produrre un prodotto finito</div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild><Button data-testid="bom-new" onClick={() => setDialogOpen(true)} className="bg-blue-700 hover:bg-blue-800 gap-2 h-9"><Plus className="w-4 h-4" />Nuova distinta</Button></DialogTrigger>
          <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Modifica distinta base' : 'Nuova distinta base'}</DialogTitle><DialogDescription>Indica i componenti necessari per 1 unità di prodotto finito.</DialogDescription></DialogHeader>
            <BomForm initial={editing} products={products} customers={customers} onSubmit={handleSubmit} onCancel={() => { setDialogOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>
      {list.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300"><GitBranch className="w-10 h-10 text-slate-300 mx-auto mb-2" /><div className="text-sm">Nessuna distinta base</div></div>
      ) : (
        <div className="space-y-3">
          {list.map(b => (
            <div key={b.id} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{b.product_sku}</span>
                    <span className="font-semibold text-slate-900">{b.product_name}</span>
                    <span className="text-[10px] uppercase font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{b.variant_name}</span>
                    {b.customer_name && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{b.customer_name}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{b.items_enriched?.length} componenti</div>
                </div>
                <div>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(b); setDialogOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => del(b)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100"><th className="text-left py-1.5 text-[10px] uppercase font-medium text-slate-500">Componente</th><th className="text-right py-1.5 text-[10px] uppercase font-medium text-slate-500">Q.tà</th><th className="text-right py-1.5 text-[10px] uppercase font-medium text-slate-500">U.M.</th></tr></thead>
                <tbody>
                  {b.items_enriched?.map((it, idx) => (
                    <tr key={idx} className="border-b border-slate-50 last:border-0">
                      <td className="py-1.5"><span className="font-mono text-xs text-slate-500 mr-1">{it.product_sku}</span><span className="text-slate-700">{it.product_name}</span></td>
                      <td className="py-1.5 text-right tabular-nums">{it.quantity}</td>
                      <td className="py-1.5 text-right text-xs text-slate-500">{it.unit_of_measure}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BomManager;
